import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";

import { Table, Row, Col, Form, Button, Spinner } from "react-bootstrap";
import { useNavigate } from "react-router-dom";

import { toast } from "react-toastify";
import { CHAT_API } from "../../../api/api";

import { IoSearchOutline } from "react-icons/io5";
import { FaSort, FaSortUp, FaSortDown } from "react-icons/fa";
import Nofound from "../../../assets/not-found.png";

import { getPageNumbers } from "../../../utils/pagination";
import { handleApiError } from "../../../utils/apiErrorHandler";
import { useSortableTable } from "../../../hooks/useSortableTable";

import "./AdminPauseChats.css";

const AdminPauseChats = () => {
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);

  // search state
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  //loading
  const [loadingPauseChats, setLoadingPauseChats] = useState(true);
  const [loadingPause, setloadingPause] = useState(false);

  // main toast error
  const showErrorToast = (message) => {
    toast.error(message, {
      position: "top-right",
      autoClose: 1500,
      theme: "colored",
    });
  };

  const fetchPauseChats = useCallback(async () => {
    const token = localStorage.getItem("token");

    if (!token) {
      setLoadingPauseChats(false);
      navigate("/admin-signin");

      return;
    }
    try {
      const response = await axios.get(CHAT_API.GET_PAUSE_CHAT_LIST, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = response.data;

      if (data.statusCode === 200) {
        setStudents(data.chatList || []);
      } else {
        // This will handle any 2xx response that is not 201
        showErrorToast(data.message);
      }
    } catch (error) {
      console.error("Error fetching pause chats:", error);
      // Server responded with non-2xx status (like 400, 500)

      // CHECK (expiry / invalid token)
      handleApiError(error, navigate, showErrorToast, "/admin-signin");
    } finally {
      setLoadingPauseChats(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchPauseChats();
  }, [fetchPauseChats]);

  const handleToggleChatStatus = async (chatRoomId, isPaused) => {
    const token = localStorage.getItem("token");

    if (!token) {
      setloadingPause(false);
      navigate("/admin-signin");

      return;
    }

    setloadingPause(true);

    // Step 1: Optimistic UI → toggle the pause_chat locally first for animation
    setStudents((prev) =>
      prev.map((item) =>
        item.chat_room_id === chatRoomId
          ? { ...item, pause_chat: !isPaused }
          : item,
      ),
    );

    // Step 2: Wait for animation (CSS transition is 0.2s)
    setTimeout(() => {
      // Remove the row from table after animation
      setStudents((prev) =>
        prev.filter((item) => item.chat_room_id !== chatRoomId),
      );
    }, 400); // 400ms matches your CSS transition

    try {
      const response = await axios.post(
        CHAT_API.UPDATE_PAUSE_CHAT,
        {
          chat_room_id: chatRoomId,
          pause_chat: !isPaused,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      const data = response.data;

      if (data.statusCode === 200) {
        // await fetchPauseChats();
      } else {
        // This will handle any 2xx response that is not 201
        showErrorToast(data.message);
      }
    } catch (error) {
      console.error("Error updating chat status:", error);
      // rollback if API fails
      await fetchPauseChats();

      // Server responded with non-2xx status (like 400, 500)

      // CHECK (expiry / invalid token)
      handleApiError(error, navigate, showErrorToast, "/admin-signin");
    } finally {
      setloadingPause(false);
    }
  };

  const filteredRows = students.filter(
    (item) =>
      item.student_name.toLowerCase().includes(search.trim().toLowerCase()) ||
      item.tutor_name.toLowerCase().includes(search.trim().toLowerCase()) ||
      item.course_title.toLowerCase().includes(search.trim().toLowerCase()),
  );

  // SORT HOOK (MAIN ADDITION)
  const { sortedData, sortConfig, handleSort } = useSortableTable(filteredRows);

  // Pagination slice
  const rowsPerPage = 5;
  const totalPages = Math.ceil(sortedData.length / rowsPerPage);

  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;

  const currentPausedChats = sortedData.slice(indexOfFirstRow, indexOfLastRow);

  const handlePageChange = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [search]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages || 1);
    }
  }, [totalPages, currentPage]);

  //  SORT ICON HELPER
  const renderSortIcon = (key) => {
    if (sortConfig.key !== key) return <FaSort size={11} />;
    if (sortConfig.direction === "asc") return <FaSortUp size={11} />;
    return <FaSortDown size={11} />;
  };

  // function call
  const pages = getPageNumbers(currentPage, totalPages);

  if (loadingPauseChats) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  return (
    <div className="admin-chat-details-section">
      <h4 className="tutor-feedback-title">Paused Conversations</h4>
      <p className="tutor-feedback-subtitle">
        Review messages, monitor conversation activity, and unblock paused
        chats.
      </p>

      <div className="admin-internshipsss-management-filters border-bottom pb-3">
        <Row className="mb-0 admin-internshipsss-management-filters-rowsss justify-content-end">
          <Col md={3}>
            <div className="admin-internshipsss-management-search-box">
              <span className="admin-internshipsss-management-search-icon">
                <IoSearchOutline />
              </span>
              <Form.Control
                type="text"
                placeholder="Search student, tutor, or course"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="admin-internshipsss-management-search-input"
              />
            </div>
          </Col>
        </Row>
      </div>

      <div className="admin-chat-details-table-wrapper">
        <div className="admin-chat-details-table-responsive">
          <Table className="mb-0">
            <thead>
              <tr>
                <th
                  onClick={() =>
                    sortedData.length > 0 && handleSort("student_name")
                  }
                  style={{
                    cursor: sortedData.length > 0 ? "pointer" : "not-allowed",
                  }}
                >
                  Student Name {renderSortIcon("student_name")}
                </th>
                <th
                  onClick={() =>
                    sortedData.length > 0 && handleSort("tutor_name")
                  }
                  style={{
                    cursor: sortedData.length > 0 ? "pointer" : "not-allowed",
                  }}
                >
                  Tutor Name {renderSortIcon("tutor_name")}
                </th>
                <th
                  onClick={() =>
                    sortedData.length > 0 && handleSort("course_title")
                  }
                  style={{
                    cursor: sortedData.length > 0 ? "pointer" : "not-allowed",
                  }}
                >
                  Course {renderSortIcon("course_title")}
                </th>

                <th>Action</th>
              </tr>
            </thead>

            <tbody>
              {/* {loadingPauseChats ? (
                <tr>
                  <td colSpan="12" style={{ textAlign: "center" }}>
                    Loading paused chats...
                  </td>
                </tr>
              ) :  */}
              {currentPausedChats.length > 0 ? (
                currentPausedChats.map((item) => (
                  <tr key={item.chat_room_id}>
                    <td>{item.student_name}</td>
                    <td>{item.tutor_name}</td>
                    <td>{item.course_title}</td>
                    <td>
                      <Form.Check
                        type="switch"
                        className="admin-chat-details-toggle"
                        checked={!item.pause_chat} // ON = Active, OFF = Paused
                        onChange={() =>
                          handleToggleChatStatus(
                            item.chat_room_id,
                            item.pause_chat,
                          )
                        }
                        disabled={loadingPause}
                      />
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="10" className="admin-chat-details-no-data">
                    <img
                      src={Nofound}
                      alt="No Data Found"
                      className="admin-all-tutor-empty-img"
                    />
                    <p className="admin-dashboard-no-data">No Data Found</p>
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
      </div>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="tutor-assignment-table-pagination">
          <Button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
          >
            Previous
          </Button>

          {pages.map((page, idx) =>
            page === "..." ? (
              <span key={idx} style={{ padding: "0 3px" }}>
                ...
              </span>
            ) : (
              <Button
                key={idx}
                active={currentPage === page}
                onClick={() => handlePageChange(page)}
              >
                {page}
              </Button>
            ),
          )}

          <Button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
};

export default AdminPauseChats;
